/**
 * Cross-cutting constants shared by backend, sync, and apps.
 * Env-provided secrets are NEVER here — only stable protocol/UX constants.
 */
/**
 * Region all Cloud Functions are deployed to (Mumbai — dealers are in India).
 * The web SDK MUST call `getFunctions(app, FUNCTIONS_REGION)` with this value or
 * callables resolve to the wrong host. Single source of truth for client+server.
 */
export const FUNCTIONS_REGION = 'asia-south1';
/** Custom auth claim that marks an admin. Set via Agent A's setAdminClaim. */
export const ADMIN_CLAIM = 'admin';
/** Header carrying the shared secret on the ingest endpoint. */
export const SYNC_SECRET_HEADER = 'x-sync-secret';
/** Max FCM tokens retained per user (oldest pruned beyond this). */
export const FCM_TOKEN_LIMIT = 10;
/** OTP resend cooldown, seconds (customer login UX). */
export const OTP_RESEND_COOLDOWN_SEC = 60;
/**
 * Max session age before a forced re-login (both apps): users whose Firebase
 * auth time is older than this are signed out and must OTP again.
 */
export const SESSION_MAX_AGE_MS = 10 * 24 * 60 * 60 * 1000; // 10 days
/**
 * Email-OTP login (FREE tier — no SMS). The Apps Script web app emails a code
 * and, on verify, mints a Firebase custom token. The provider was retired in favor of
 * Clerk login (the values remain for the client-side fallback code).
 */
export const EMAIL_OTP_CODE_LENGTH = 6;
/** How long an emailed code stays valid, ms. */
export const EMAIL_OTP_TTL_MS = 10 * 60_000;
/** Wrong-code attempts allowed before the code is invalidated. */
export const EMAIL_OTP_MAX_ATTEMPTS = 5;
/**
 * Uid scheme for email-OTP users: `em_` + sha256hex(normalized email). Both the
 * Apps Script token minting and the emulator dev sign-in derive uids this way,
 * so the same email always maps to the same Firebase uid.
 */
export const EMAIL_OTP_UID_PREFIX = 'em_';
/** Debounce window for the Apps Script onEdit trigger, ms. */
export const SYNC_DEBOUNCE_MS = 5_000;
/** FCM multicast batch size limit (Firebase hard limit is 500). */
export const FCM_MULTICAST_BATCH = 500;
/** "Recently updated" flash window used by the customer app, ms. */
export const RECENT_UPDATE_WINDOW_MS = 30_000;
/** How often the customer app refreshes its presence heartbeat, ms. */
export const PRESENCE_HEARTBEAT_MS = 60_000;
/**
 * A presence doc counts as "online" only if its lastSeen is newer than this.
 * Should be > heartbeat interval so a single missed beat doesn't flip offline.
 */
export const PRESENCE_ONLINE_WINDOW_MS = 150_000;
/** Insights thresholds (admin panel). */
/**
 * Most products a dealer may keep on their wishlist.
 *
 * The list lives on the user document, which the admin panel streams for every
 * dealer, so the cap is what keeps those documents small (100 ids is ~4 KB).
 * It also bounds the damage if a client ever loops on the toggle.
 */
export const WISHLIST_MAX = 100;
export const INSIGHTS = {
    /** Views of the same product to count as a "hot lead". */
    hotLeadViewCount: 3,
    /** Days of inactivity to count as "churn risk". */
    churnRiskDays: 14,
    /** Window (days) for "most active" / active-audience calculations. */
    activeWindowDays: 7,
    /** Products to list in the "Most wishlisted" card. */
    topWishlisted: 10,
};
/* ------------------------------ scratch & win ----------------------------- */
/**
 * The launch prize table (Sep 2026). Loaded by the admin page's "Use default
 * prizes" button and by the seeds; the live table is whatever the admin last
 * saved in `app_config/scratchCard`. Weights are percentages and add up to 100.
 */
export const DEFAULT_SCRATCH_PRIZES = [
    {
        id: 'cashback-250',
        title: '₹250 cashback',
        description: 'On your next order of an HP laptop, desktop or AIO. Valid for 2 months.',
        kind: 'cashback',
        weight: 30,
        validityDays: 60,
    },
    {
        id: 'cashback-500',
        title: '₹500 cashback',
        description: 'On your next order of an HP laptop, desktop or AIO. Valid for 2 months.',
        kind: 'cashback',
        weight: 10,
        validityDays: 60,
    },
    {
        id: 'hp-wired-mouse',
        title: 'HP wired mouse',
        description: 'Collect it with your next order.',
        kind: 'product',
        weight: 35,
    },
    {
        id: 'hp-wireless-combo',
        title: 'HP wireless keyboard + mouse combo',
        description: 'Collect it with your next order.',
        kind: 'product',
        weight: 5,
    },
    {
        id: 'better-luck',
        title: 'Better luck next time',
        kind: 'none',
        weight: 20,
    },
];
/**
 * Fraction of the scratch layer that must be cleared before the app counts the
 * card as revealed (and stamps `revealedAt`). Below this the dealer is still
 * scratching; above it the rest of the foil is cleared for them.
 */
export const SCRATCH_REVEAL_THRESHOLD = 0.55;
/** Round ids are used in doc ids, so they are kept to this shape. */
export const SCRATCH_ROUND_PATTERN = /^[a-z0-9][a-z0-9-]{0,39}$/;
//# sourceMappingURL=constants.js.map